import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { euros } from "../lib/formato";
import { nombresDeIdiomas, SelectorIdioma, useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";
import MostradorDemo from "./MostradorDemo";
import "@fontsource-variable/bricolage-grotesque/opsz.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";

/**
 * La página que ve quien llega a la web sin haber entrar nunca.
 *
 * Antes aquí había un formulario de entrar. Un club que llegaba por
 * recomendación se encontraba una casilla de usuario y contraseña, sin una
 * línea que dijera qué es esto ni cómo se consigue. Estábamos vendiendo un
 * producto cuya puerta era un login.
 *
 * Después hubo una página de producto: titular, pantalla al lado, filas que
 * alternan de lado, lista con ✓, tres pasos. Bien hecha, y aun así se veía a
 * la legua que era una plantilla, porque es la misma que tienen todas.
 *
 * Esto es lo contrario: una frase, el mostrador de verdad para tocarlo, y lo
 * demás contado en texto llano, como lo contaría una persona. Sin tarjetas,
 * sin filas alternas, sin banda oscura. Lo que convence a un club es usar el
 * programa treinta segundos, no leer que existe.
 *
 * En castellano y en catalán: una parte grande de los clubes está en
 * Cataluña, y la primera página es donde se decide si siguen leyendo. Los
 * textos viven en src/idiomas.
 *
 * Todo lo que se cuenta aquí lo hace el programa hoy. La tentación de adornar
 * es enorme y es mal negocio: quien se da de alta por algo que no existe se va
 * en una semana, y encima habiendo dado de alta a sus socios.
 *
 * Las fuentes van con el programa, no se piden a Google: la política de
 * privacidad dice que no se manda nada a terceros, y la CSP (`font-src 'self'`)
 * lo impide de todas formas.
 */

interface Tramo {
  desde: number;
  hasta: number | null;
  /** `null` en el tramo grande: ahí no se anuncia precio, se habla. */
  precioCentimos: number | null;
  nombre: string;
}

interface Registro {
  abierto: boolean;
  exigeCodigo: boolean;
  tramos: Tramo[];
  diasDePrueba: number;
  contacto: string | null;
}

/** «100 €» y no «100,00 €»: en un precio anunciado los céntimos son ruido. */
function precioCorto(centimos: number): string {
  return euros(centimos).replace(",00", "");
}

/*
 * Lo que hay detrás del mostrador, contado en párrafos cortos con la palabra
 * delante. Es la forma del README, que es la forma en que se cuenta esto
 * cuando se cuenta de palabra.
 */
const LO_DEMAS = [
  "socios",
  "limites",
  "inventario",
  "caja",
  "cartera",
  "barra",
  "portal",
  "cuotas",
  "gastos",
  "acceso",
  "informes",
  "carta",
] as const;

export default function Inicio() {
  const { t, dias: enDias, idioma } = useIdioma();

  /*
   * El título de la pestaña es el mismo para las cuatro aplicaciones y dice
   * "Gestión del club", que en la pantalla que ve un posible cliente sobra.
   * Aquí, y solo aquí, se pone el nombre del producto.
   */
  useEffect(() => {
    const anterior = document.title;
    document.title = t("titulo.web");
    return () => {
      document.title = anterior;
    };
  }, []);

  const registro = useQuery({
    queryKey: ["registro-publico"],
    queryFn: () => api<Registro>("/auth/registro"),
  });

  const tramos = registro.data?.tramos ?? [];
  const dias = registro.data?.diasDePrueba ?? 0;

  /*
   * El boton grande tiene que llevar a donde de verdad se puede entrar.
   *
   * Con el alta cerrada por codigo —que es como se vende esto: uno a uno— un
   * "Probar 30 dias gratis" acaba en un formulario que pide un codigo que el
   * visitante no tiene. Prometer una puerta abierta y poner un portero detras
   * es la forma mas rapida de perder a alguien que ya estaba convencido. Asi
   * que se le lleva a pedir la prueba: un formulario que le llega al
   * proveedor y que se contesta con el codigo. Antes era un mailto:, y quien
   * no tiene el correo montado en ese ordenador se quedaba en nada.
   */
  const exigeCodigo = registro.data?.exigeCodigo ?? false;

  const llamada = exigeCodigo
    ? {
        texto: dias > 0 ? t("web.pedirPrueba") : t("web.pedirAcceso"),
        enlace: "/prueba",
      }
    : {
        texto:
          dias > 0
            ? t("web.probarGratis", { dias: enDias(dias) })
            : t("web.crearClub"),
        enlace: "/registro",
      };

  const Boton = ({ clase }: { clase: string }) => (
    <Link to={llamada.enlace} className={clase}>
      {llamada.texto}
    </Link>
  );

  /*
   * El nombre del tramo se compone aquí y no se usa el del servidor: el
   * servidor lo manda en castellano, y en la pantalla en catalán quedaría un
   * «Hasta 299 socios» suelto en medio de la tabla.
   */
  const nombreDelTramo = (tramo: Tramo) => {
    if (tramo.hasta === null)
      return t("web.tramo.mas", { desde: tramo.desde - 1 });
    if (tramo.desde === 0) return t("web.tramo.hasta", { hasta: tramo.hasta });
    return t("web.tramo.entre", { desde: tramo.desde, hasta: tramo.hasta });
  };

  return (
    <div className="publica" lang={idioma}>
      <header className="publica-barra">
        <Link to="/" className="publica-marca">
          <img src="/asocis.png" alt="asocis" className="publica-logotipo" />
        </Link>
        <div className="fila" style={{ gap: 10 }}>
          <SelectorIdioma compacto />
          <Link to="/entrar" className="boton secundario">
            {t("web.entrar")}
          </Link>
        </div>
      </header>

      <main className="hoja">
        <h1 className="frase">{t("web.frase")}</h1>
        <p className="frase-segunda">{t("web.fraseSegunda")}</p>
        <div className="acciones">
          <Boton clase="boton grande" />
          {exigeCodigo && (
            <span className="suave">
              {t("web.altasUnaAUna")}{" "}
              <Link to="/registro">{t("web.yaTienesUno")}</Link>
            </span>
          )}
        </div>

        <div className="hoja-ancha">
          <MostradorDemo />
        </div>
        <p className="suave pie-demo">
          {t("web.pieDemo")}
          {/*
           * El mostrador dibujado va en castellano y no se traduce: es un
           * dibujo con datos inventados, no el programa, y mantenerlo en cinco
           * idiomas sería mantener cinco veces un texto que no usa nadie. El
           * programa de verdad sí está en todos; se dice aquí y ya.
           */}
          {idioma !== "es" && <> {t("web.demoEnCastellano", { idiomas: nombresDeIdiomas() })}</>}
        </p>

        <h2>{t("web.detras")}</h2>
        <div className="parrafos">
          {LO_DEMAS.map((clave) => (
            <p key={clave}>
              <strong>{t(`web.detras.${clave}` as Clave)}.</strong>{" "}
              {t(`web.detras.${clave}.texto` as Clave, { idiomas: nombresDeIdiomas() })}
            </p>
          ))}
        </div>

        <h2 id="precio">{t("web.precio")}</h2>
        {/* La única frase con negrita dentro; el resto va en texto llano. */}
        <p dangerouslySetInnerHTML={{ __html: t("web.precio.texto") }} />
        {tramos.length > 0 ? (
          <table className="tarifa">
            <thead>
              <tr>
                <th>{t("web.precio.sociosActivos")}</th>
                <th className="num">{t("web.precio.alMes")}</th>
              </tr>
            </thead>
            <tbody>
              {tramos.map((tramo) => (
                <tr key={tramo.desde}>
                  <td>{nombreDelTramo(tramo)}</td>
                  <td className="num">
                    {tramo.precioCentimos === null ? (
                      <span className="tarifa-hablamos">
                        {t("web.precio.hablamos")}
                      </span>
                    ) : (
                      <strong>{precioCorto(tramo.precioCentimos)}</strong>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="tarifa-vacia">{t("web.precio.consultanos")}</p>
        )}
        <p>
          {dias > 0 && <>{t("web.precio.prueba", { dias: enDias(dias) })} </>}
          {t("web.precio.condiciones")}
        </p>

        <h2>{t("web.datos")}</h2>
        <p>{t("web.datos.texto")}</p>
        <p className="suave">{t("web.datos.nota")}</p>

        <h2>{t("web.empezar")}</h2>
        <p>
          <strong>
            {exigeCodigo
              ? t("web.empezar.nosEscribes")
              : t("web.empezar.creasClub")}
          </strong>{" "}
          {exigeCodigo
            ? t("web.empezar.conCodigo")
            : t("web.empezar.sinCodigo")}{" "}
          {t("web.empezar.despues")}
        </p>
        <div className="acciones">
          <Boton clase="boton grande" />
        </div>
      </main>

      <footer className="publica-pie">
        <span>{t("web.pie")}</span>
        <span className="publica-pie-enlaces">
          <Link to="/aviso-legal">{t("web.pie.avisoLegal")}</Link>
          <Link to="/privacidad">{t("web.pie.privacidad")}</Link>
          {registro.data?.contacto && (
            <a href={`mailto:${registro.data.contacto}`}>
              {registro.data.contacto}
            </a>
          )}
        </span>
      </footer>
    </div>
  );
}
